import { useState } from "react";
import { Badge, Button } from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import type { Amount } from "../admin-client";
import { pageOf } from "./paging";
import type { PagedRows, Paging } from "./paging";

export { pageOf };
export type { PagedRows, Paging };

/**
 * The bits every admin screen renders the same way.
 *
 * Here rather than copied into four screens because consistency is the
 * safety property: "revoked" has to look like "revoked" on the users list,
 * the device table and the application page, or an operator scanning for it
 * misses one.
 */

/** A timestamp, or an em dash. An empty cell reads as a page that failed. */
export function when(value: string | undefined | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

/**
 * Status drives the colour, and the dead states are the loud ones: an
 * operator scanning a table is looking for the row that is suspended,
 * disabled or revoked, and it must not look like every other line.
 */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case "active":
    case "available":
    case "valid":
      return "success";
    case "pending":
    case "expired":
    // A deprecated model still answers and a rotating credential still works.
    // Both are "act before this becomes a problem", which is warning, not
    // danger — colouring them red next to a genuinely dead row teaches an
    // operator to ignore red.
    case "deprecated":
    case "rotating":
    // A tier whose renewal failed, or that is set to end, still serves.
    case "past_due":
    case "cancelling":
      return "warning";
    case "disabled":
    case "suspended":
    case "revoked":
    case "deleted":
    case "retired":
    case "invalid":
      return "danger";
    default:
      return "neutral";
  }
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={statusTone(status)}>{status}</Badge>;
}

/** Scopes as code, one per line: a comma-joined string of six scopes is a
 *  paragraph nobody reads, and scopes are what an operator is checking. */
export function ScopeList({ scopes }: { scopes: string[] }) {
  if (scopes.length === 0) {
    return <span className="esm-muted-text">none</span>;
  }
  return (
    <ul className="esm-scope-list">
      {scopes.map((scope) => (
        <li key={scope}>
          <code>{scope}</code>
        </li>
      ))}
    </ul>
  );
}

/**
 * Health is not status, so it does not share statusTone.
 *
 * An account can be `active` and `down` at the same time — that pair is
 * exactly the incident an operator is scanning for — and rendering both from
 * one colour table would have them agree when they do not.
 */
export function healthTone(health: string): BadgeTone {
  switch (health) {
    case "healthy":
      return "success";
    case "degraded":
      return "warning";
    case "down":
      return "danger";
    default:
      return "neutral";
  }
}

export function HealthBadge({ health }: { health: string }) {
  return <Badge tone={healthTone(health)}>{health}</Badge>;
}

/** A boolean as a badge, stated either way. "fallback off" reads as a
 *  decision somebody made; a missing badge reads as a page that did not
 *  finish loading. */
export function FlagBadge({ on, label }: { on: boolean; label: string }) {
  return (
    <Badge tone={on ? "success" : "neutral"}>
      {label} {on ? "on" : "off"}
    </Badge>
  );
}

/** A count, or the fact that there is no limit. Null means "uncapped" on
 *  every limit field the admin API sends, and an em dash would read as
 *  "unknown". */
export function limit(value: number | null | undefined): string {
  return value === null || value === undefined ? "no limit" : value.toLocaleString();
}

/**
 * A micros figure, shown as micros.
 *
 * The wire sends these limits without a currency, so there is nothing to
 * divide them into and no symbol to put in front of them. Printing the number
 * the platform actually stores is honest; inventing "$12.00" from a figure
 * whose currency this screen never learned is not.
 */
export function microsLimit(value: number | null | undefined): string {
  return value === null || value === undefined
    ? "no limit"
    : `${value.toLocaleString()} micros`;
}

/**
 * An amount, rendered as the server sent it.
 *
 * `amount` is a decimal STRING the server formatted from the integer micros
 * it stores; this console prints it and does not parse it. A portal that
 * turned it into a number to format it would introduce a float exactly where
 * the whole platform has been careful not to have one — and the currency
 * beside it is not decoration, because a suite-wide total of USD and GBP is
 * the one sum this product must never show.
 */
export function Money({ value }: { value: Amount }) {
  return (
    <span className="esm-amount">
      {value.amount} <span className="esm-muted-text">{value.currency}</span>
    </span>
  );
}

/** An optional amount. Absent stays an em dash: "not charged yet" and
 *  "charged nothing" are different facts and must not look the same. */
export function OptionalMoney({ value }: { value: Amount | undefined }) {
  return value ? <Money value={value} /> : <span className="esm-muted-text">—</span>;
}

/**
 * A signed ledger amount, with the sign kept.
 *
 * A debit is negative in the database and reads as negative here. Rendering
 * magnitudes and a direction word would be prettier and would put the one
 * transformation between the ledger and the operator's eye that a
 * reconciliation argument later has to unpick.
 */
export function SignedMoney({ value }: { value: Amount }) {
  const tone = value.micros < 0 ? "esm-amount--debit" : value.micros > 0 ? "esm-amount--credit" : "";
  return (
    <span className={`esm-amount ${tone}`.trim()}>
      {value.micros > 0 ? "+" : ""}
      {value.amount} <span className="esm-muted-text">{value.currency}</span>
    </span>
  );
}

/** An id, shortened for a table but complete on hover and complete when
 *  copied — an operator pasting a truncated uuid into a query gets nothing,
 *  and does not find out why. */
export function ShortID({ value }: { value: string | undefined }) {
  if (!value) {
    return <span className="esm-muted-text">—</span>;
  }
  return (
    <code className="esm-short-id" title={value}>
      {value.slice(0, 8)}
    </code>
  );
}

/**
 * A full identifier with a button that copies it.
 *
 * ShortID above is for a TABLE, where eight characters plus a title is the
 * right trade. This is for the handful of ids an operator has to carry OUT of
 * this console and paste into somebody else's — a Stripe `cus_` or `sub_` —
 * and for those, truncation is the failure mode: a partial id pasted into the
 * Stripe dashboard finds nothing and does not say why. The whole value is
 * rendered and the whole value is copied.
 *
 * The button is a courtesy over a value that is already selectable, so it
 * degrades to nothing useful being lost when `navigator.clipboard` is absent
 * (an insecure origin, an old browser): the text is still there to select.
 * "Copied" goes into a `role="status"` region rather than only into the icon,
 * because a change of glyph is not an announcement.
 */
export function CopyText({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <span className="esm-copy">
      <code>{value}</code>
      <button
        type="button"
        className="esm-copy__button"
        aria-label={`Copy ${label}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <span className="esm-visually-hidden" role="status">
        {copied ? `${label} copied` : ""}
      </span>
    </span>
  );
}

/**
 * A timestamp with its milliseconds kept.
 *
 * `when` above is for a row an operator reads one of; this is for a TRAIL,
 * where the ordering between two lines is the fact being read. One sign-in
 * writes several security events inside one transaction at one `now()`, and
 * `toLocaleString()` alone renders everything inside a second as the same
 * instant — which turns "the refusal came after the refresh" into a question
 * the screen cannot answer. The Admin API sends sub-second precision on
 * purpose; this is the render that does not throw it away.
 *
 * Milliseconds and not microseconds because a JS Date holds nothing finer.
 * Two rows written at one `now()` are genuinely simultaneous, so they are
 * meant to print identically — the id tiebreak in the server's ORDER BY is
 * what keeps their order stable.
 */
export function whenExact(value: string | undefined | null): string {
  if (!value) {
    return "—";
  }
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) {
    return "—";
  }
  return `${at.toLocaleString()}.${String(at.getMilliseconds()).padStart(3, "0")}`;
}

/**
 * toRFC3339 turns what a `datetime-local` input holds — a wall-clock string
 * with no offset — into an instant.
 *
 * `new Date("2026-08-24T09:00")` is interpreted in the BROWSER's timezone,
 * which is what the operator meant when they typed it, and `toISOString()`
 * states that same moment in UTC, which is what the server accepts. Sending
 * the raw string would be sending a time with no timezone at all, and the
 * server refuses it rather than picking one.
 *
 * Shared by the audit trail and the user page's event trail: both take a
 * half-open window through the same kind of box, and two answers to "what
 * does this box send" is one answer too many.
 */
export function toRFC3339(local: string | undefined): string | undefined {
  if (!local) {
    return undefined;
  }
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/**
 * The page index, held for a screen.
 *
 * SPLIT FROM THE ARITHMETIC, and the split is not stylistic. The rows a screen
 * pages are usually derived from a query, so they do not exist until after its
 * `isPending` and `error` early returns — and a hook called below an early
 * return runs on some renders and not others, which React fails with "rendered
 * fewer hooks than expected" the moment the query resolves. This sits with the
 * screen's other `useState` calls, above every return; `pageOf` runs wherever
 * the rows finally are. It is pure and lives in `paging.ts`, where it is
 * self-checked.
 */
export function usePaging(pageSize: number): Paging {
  const [page, setPage] = useState(0);
  return { page, setPage, pageSize };
}

/**
 * The paging controls, and the count that makes them honest.
 *
 * The count is not decoration. A table showing twenty-five of six hundred rows
 * with no total looks exactly like a table showing all twenty-five rows there
 * are, and an operator who concludes the second is an operator who thinks a
 * model is missing from the catalogue.
 *
 * Renders NOTHING on a single page. Controls that are always disabled teach
 * people to stop looking at them.
 */
export function Pagination<Row>({ paged, noun }: { paged: PagedRows<Row>; noun: string }) {
  if (paged.pageCount <= 1) {
    return null;
  }
  return (
    <nav className="esm-pagination" aria-label={`${noun} pages`}>
      <span className="esm-muted-text">
        Showing {paged.firstShown}–{paged.lastShown} of {paged.total} {noun}
      </span>
      <div className="esm-pagination__controls">
        <Button
          variant="ghost"
          onClick={() => paged.setPage(paged.page - 1)}
          disabled={paged.page === 0}
        >
          Previous
        </Button>
        {/* Announced, so a screen reader hears the page change rather than only
            seeing the table's rows silently replaced. */}
        <span aria-live="polite">
          Page {paged.page + 1} of {paged.pageCount}
        </span>
        <Button
          variant="ghost"
          onClick={() => paged.setPage(paged.page + 1)}
          disabled={paged.page >= paged.pageCount - 1}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}
